import React, { useEffect, useMemo, useState } from 'react';

interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  category: string;
  required_fields: string[];
}

interface DocumentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    formData: Record<string, string>,
    action?: 'download' | 'print' | 'email',
    emailTo?: string
  ) => void | Promise<void>;
  template: TemplateMetadata;
  initialData?: Record<string, string>;
  vehicleId?: string;
  customerId?: string;
  generating?: boolean;
  showEmailOption?: boolean;
  defaultEmailTo?: string;
}

const toLabel = (field: string): string => {
  return field
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const DocumentFormModal: React.FC<DocumentFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  template,
  initialData,
  vehicleId,
  customerId,
  generating = false,
  showEmailOption = false,
  defaultEmailTo,
}) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [emailTo, setEmailTo] = useState('');

  const requiredFields = useMemo(() => {
    return Array.isArray(template.required_fields) ? template.required_fields : [];
  }, [template.required_fields]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextData: Record<string, string> = {
      ...(initialData || {}),
    };

    requiredFields.forEach((field) => {
      if (!(field in nextData)) {
        nextData[field] = '';
      }
    });

    if (vehicleId && !nextData.vehicle_id) {
      nextData.vehicle_id = vehicleId;
    }

    if (customerId && !nextData.customer_id) {
      nextData.customer_id = customerId;
    }

    setFormData(nextData);
    setEmailTo(defaultEmailTo || '');
  }, [isOpen, initialData, requiredFields, vehicleId, customerId, defaultEmailTo]);

  if (!isOpen) {
    return null;
  }

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onSubmit(formData, 'download', emailTo);
  };

  const handleAction = async (action: 'print' | 'email') => {
    if (action === 'email' && !emailTo.trim()) {
      return;
    }
    await onSubmit(formData, action, emailTo.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Generate {template.name}</h3>
          <p className="mt-1 text-sm text-gray-500">{template.description}</p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {requiredFields.length === 0 ? (
            <p className="text-sm text-gray-600">
              No additional fields are required for this template.
            </p>
          ) : (
            requiredFields.map((field) => (
              <div key={field}>
                <label htmlFor={field} className="mb-1 block text-sm font-medium text-gray-700">
                  {toLabel(field)}
                </label>
                <input
                  id={field}
                  type="text"
                  value={formData[field] || ''}
                  onChange={(event) => handleChange(field, event.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))
          )}

          {showEmailOption && (
            <div>
              <label htmlFor="email_to" className="mb-1 block text-sm font-medium text-gray-700">
                Recipient Email
              </label>
              <input
                id="email_to"
                type="email"
                value={emailTo}
                onChange={(event) => setEmailTo(event.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="customer@example.com"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={generating}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={generating}
              onClick={() => handleAction('print')}
              className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
            >
              {generating ? 'Working...' : 'Print'}
            </button>
            {showEmailOption && (
              <button
                type="button"
                disabled={generating || !emailTo.trim()}
                onClick={() => handleAction('email')}
                className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
              >
                {generating ? 'Working...' : 'Send Email'}
              </button>
            )}
            <button
              type="submit"
              disabled={generating}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {generating ? 'Generating...' : 'Generate PDF'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentFormModal;
